import { NextSeo } from 'next-seo'
import useTranslation from 'next-translate/useTranslation'
import { useCallback } from 'react'
import { useRecoilState } from 'recoil'
import { useQuery } from '@/app/common/graphql/hooks'
import {
  buildDealCalculationVariables,
  useCheckCartInvalid,
} from '@/domains/checkout/checkout.service'
import {
  checkoutDateState,
  dealCalculationState,
  dealDurationInDaysState,
  dealTypeState,
  selectedItemsState,
} from '@/domains/checkout/checkout.state'
import { SingleCheckoutSection } from '@/domains/checkout/sections/SingleCheckoutSection'
import * as tracking from '@/helpers/tracking'
import useCompany from '@/helpers/useCompany'
import {
  DealCalculationDocument,
  DealCalculationQuery,
  DealCalculationQueryVariables,
  EDealType,
  ETransportMode,
  PersistedDealCalculation,
} from '@/types/gql/graphql'

const defaultPickupTransportMode = ETransportMode.Shop

export function SingleCheckoutPage() {
  const { t } = useTranslation()
  const [checkoutDate] = useRecoilState(checkoutDateState)
  const company = useCompany()
  const [dealCalculation, setDealCalculationRecoil] =
    useRecoilState(dealCalculationState)
  const [selectedItems] = useRecoilState(selectedItemsState)
  const [dealType, setDealType] = useRecoilState(dealTypeState)
  const [durationInDays] = useRecoilState(dealDurationInDaysState)

  const setDealCalculation = useCallback(
    (calculation: PersistedDealCalculation | undefined) => {
      if (
        calculation &&
        selectedItems.length === calculation.itemsValuesEntries.length
      ) {
        tracking.beginCheckout(
          selectedItems.map((item, i) => ({
            ...item,
            actualValue:
              calculation.itemsValuesEntries[i].payoutAmount || undefined,
          })),
        )
      }

      setDealCalculationRecoil(calculation)
    },
    [setDealCalculationRecoil, selectedItems],
  )

  const isCartInvalid = useCheckCartInvalid()
  const dealCalculationPawn = useQuery<
    DealCalculationQuery,
    DealCalculationQueryVariables
  >(DealCalculationDocument, {
    variables: {
      ...buildDealCalculationVariables({
        companyId: company?._id,
        dealType: EDealType.Pawn,
        date: checkoutDate,
        items: isCartInvalid ? [] : selectedItems,
        pickupTransportMode: defaultPickupTransportMode,
        durationInDays,
      }),
    },
    skip: !company || !durationInDays,
    onCompleted: (data) => {
      if (data && dealType === EDealType.Pawn) {
        setDealCalculation(data.dealCalculation)
      }
    },
  })

  const dealCalculationPurchase = useQuery<
    DealCalculationQuery,
    DealCalculationQueryVariables
  >(DealCalculationDocument, {
    variables: buildDealCalculationVariables({
      companyId: company?._id,
      dealType: EDealType.Purchase,
      date: checkoutDate,
      items: isCartInvalid ? [] : selectedItems,
      pickupTransportMode: defaultPickupTransportMode,
      durationInDays: 0,
    }),
    skip: !company || !durationInDays,
    onCompleted: (data) => {
      if (data && dealType === EDealType.Purchase) {
        setDealCalculation(data.dealCalculation)
      }
    },
  })

  function handleDealTypeChange(value: EDealType) {
    setDealType(value)
    if (value === EDealType.Purchase) {
      setDealCalculation(dealCalculationPurchase.data?.dealCalculation)
    } else if (value === EDealType.Pawn) {
      setDealCalculation(dealCalculationPawn.data?.dealCalculation)
    }
  }

  return (
    <div className="overflow-x-clip bg-fill-senary min-h-screen">
      <NextSeo
        noindex={true}
        nofollow={true}
        title={t(
          dealType === EDealType.Pawn
            ? 'checkout:pawn_preview'
            : 'checkout:purchase_preview',
        )}
      />
      <SingleCheckoutSection
        dealCalculation={dealCalculation}
        dealType={dealType}
        handleDealTypeChange={handleDealTypeChange}
      />
    </div>
  )
}
